/* eslint-disable @typescript-eslint/no-explicit-any */

import { FormEvent, useEffect, useState } from 'react';
import {
  Button,
  Container,
  DropdownField,
  ExportTableButton,
} from '@/components';

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { Nullable } from 'primereact/ts-helpers';
import { getArmadoresList } from '@/services/getListArmadores';

import { useAuthContext } from '@/hooks';
import { TableData } from '@/components/Table/types';
import { getStockStatusList } from '@/services/getStockStatusList';
import { getDetailedStock } from './getDetailedStockList';

import { DetailedStockInfo } from './types';

import DetailedStockTable from './components/DetailedStockTable/DetailedStockTable';
import { columnsToExport } from './columnsToExport';
import styles from './DetailedStock.module.scss';

const FILE_NAME = `DetailedStock-Montecon`;
export type TableFilters = {
  estado: Nullable<Date>;
};

const DetailedStock = () => {
  const { t } = useTranslation('detailedStock');
  const { userDataDecoded } = useAuthContext();

  const getDetailedStockMutation = useMutation({
    mutationFn: getDetailedStock,
  });

  const [selectedShipOwner, setSelectedShipOwner] = useState<string | null>();
  const [selectedEstado, setSelectedEstado] = useState<string | null>();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedShipOwner && selectedEstado) {
      getDetailedStockMutation.mutate({
        armador: selectedShipOwner,
        estado: selectedEstado,
      });
    }
  };

  const submitButtonText = t('query');

  const isTableHidden =
    !getDetailedStockMutation.data && !getDetailedStockMutation.isPending;

  const handleDropdownChange = (e: { value: string | null }) => {
    setSelectedShipOwner(e?.value);
  };

  const handleDropdownEstadoChange = (e: { value: string | null }) => {
    setSelectedEstado(e?.value);
  };

  const options = userDataDecoded?.armadores?.split(';').map((val) => {
    return {
      value: val,
      name: val,
    };
  });

  const [parsedData, setParsedData] = useState<DetailedStockInfo[]>([]);

  useEffect(() => {
    if (getDetailedStockMutation.data) {
      const newData = getDetailedStockMutation?.data?.map((item: any) => {
        const { ...rest } = item;
        return {
          ...rest,
          llegadaImport: item?.llegadaImport
            ? new Date(item?.llegadaImport)
            : '',
          llegadaExport: item?.llegadaExport
            ? new Date(item?.llegadaExport)
            : '',
        };
      });
      setParsedData(newData);
    }
  }, [getDetailedStockMutation.data]);

  const { data: assignListDropdown, isLoading: armadoresLoading } = useQuery({
    queryKey: ['armadoresListDropdown'],
    queryFn: getArmadoresList,
  });

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['statusListDropdown'],
    queryFn: getStockStatusList,
  });

  const shipownerOptions = assignListDropdown?.map((val: any) => {
    return {
      value: val.codigo,
      name: val.nombre,
    };
  });

  const statusDropdown = [
    ...(status?.map((val: any) => ({
      value: val?.descripcion,
      name: val?.descripcion,
    })) || []),
  ];

  const shipowner =
    userDataDecoded?.armadores === '*' ? shipownerOptions : options;

  return (
    <Container title={t('title')} className={styles.containerList}>
      <header>
        <form onSubmit={handleSubmit}>
          <DropdownField
            id="selectedShipOwner"
            options={shipowner}
            onChange={(e) => handleDropdownChange(e)}
            placeholder={t('selectShipowner')}
            label={t('shipowner')}
            value={selectedShipOwner}
            className={styles.dropdown}
            required
            isLoading={armadoresLoading}
          />

          <DropdownField
            id="selectedEstado"
            options={[...statusDropdown, { value: 'Todos', name: 'Todos' }]}
            onChange={(e) => handleDropdownEstadoChange(e)}
            placeholder={t('status')}
            label={t('status')}
            value={selectedEstado}
            className={styles.dropdown}
            required
            isLoading={statusLoading}
          />
          <Button
            type="submit"
            className={styles.submitButton}
            disabled={!selectedShipOwner || !selectedEstado}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.icon} />
            {submitButtonText}
          </Button>
        </form>
        {!isTableHidden && (
          <ExportTableButton
            data={getDetailedStockMutation.data as TableData}
            isDisabled={getDetailedStockMutation.isPending}
            columnsToExport={columnsToExport}
            fileName={FILE_NAME}
          />
        )}
      </header>
      {!isTableHidden && (
        <DetailedStockTable
          data={parsedData}
          isLoading={getDetailedStockMutation.isPending}
        />
      )}
    </Container>
  );
};

export default DetailedStock;
